import React from "react";
import { PageHeader } from "./About";
import { CTABanner } from "../components/Sections";
import { ShieldCheck, BookOpen, Activity, Target, ArrowRight, CheckCircle2 } from "lucide-react";
import { services } from "../mock";
import { Button } from "../components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";

const iconMap = { ShieldCheck, BookOpen, Activity, Target };

const creditEducationFaqs = [
  { q: "What is a credit score and how is it calculated?", a: "A credit score is a 3-digit number (300\u2013850) lenders use to gauge your creditworthiness. It's calculated from five factors: payment history (35%), amounts owed/utilization (30%), length of credit history (15%), new credit (10%), and credit mix (10%)." },
  { q: "What's the difference between FICO and VantageScore?", a: "FICO is the most widely used score by lenders (especially mortgages). VantageScore is the model most free credit-monitoring apps display. The numbers can differ by 20\u201380 points but both move in the same direction when you fix your report." },
  { q: "How often should I check my credit reports?", a: "At minimum every 90 days. You're entitled to a free weekly report from each of the three bureaus at AnnualCreditReport.com. We recommend a monitoring service so you catch changes (and identity theft) instantly." },
  { q: "What is credit utilization and what's the magic number?", a: "Utilization = balance \u00f7 limit. Keep total utilization under 30%, and your highest-impact card under 10%. Paying balances down before the statement closes is the single fastest way to boost a score." },
  { q: "Does closing an old credit card hurt my score?", a: "Often yes \u2014 it shortens average age of accounts and shrinks your total available credit (raising utilization). Keep older accounts open unless they carry an annual fee that isn't worth it." },
  { q: "How long do negative items stay on my report?", a: "Most negatives fall off after 7 years (collections, charge-offs, late payments). Chapter 7 bankruptcy stays 10 years. Hard inquiries fall off after 2 years. We can dispute any item that's inaccurate before then." },
  { q: "Will paying off a collection raise my score?", a: "Not always. Under newer FICO 9 and VantageScore 4.0 models, paid collections are ignored. But older FICO models still count them. We'll guide you on \"pay for delete\" negotiations whenever it makes strategic sense." },
  { q: "What's the fastest way to build credit if I'm starting from zero?", a: "Open a secured credit card, become an authorized user on a family member's well-aged account, and add a credit-builder loan. Use 1\u20139% of your limit and pay in full every month. Most people see a usable score within 6 months." }
];

const Services = () => (
  <main>
    <PageHeader title="Services" subtitle="OUR FEATURES" />

    <section className="py-20">
      <div className="max-w-7xl mx-auto px-4">
        <div className="text-center max-w-2xl mx-auto mb-14">
          <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">OUR SERVICES</p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy mb-4 text-center">Tailored solutions for every credit goal</h2>
          <p className="text-knight-navy/70 leading-relaxed">At Knight Credit Consultants, we offer a range of tailored services to support you on your path to financial health. Our focus is on delivering personalized solutions designed to meet your credit goals.</p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map(s => {
            const Icon = iconMap[s.icon];
            return (
              <div key={s.id} id={s.title.toLowerCase().replace(/\s+/g, "-")} className="card-intense group relative bg-white rounded-3xl border border-slate-200 p-8 overflow-hidden scroll-mt-24">
                <div className="absolute top-6 right-6 font-display text-6xl font-bold text-slate-100 group-hover:text-knight-gold/20 transition-colors">{s.id}</div>
                <div className="relative flex gap-5">
                  <div className="w-16 h-16 rounded-2xl bg-knight-navy text-knight-gold flex-shrink-0 flex items-center justify-center group-hover:bg-knight-gold group-hover:text-knight-navy transition-colors">
                    <Icon size={28} />
                  </div>
                  <div>
                    <h3 className="font-display font-bold text-2xl text-knight-navy mb-3">{s.title}</h3>
                    <p className="text-knight-navy/75 leading-relaxed">{s.description}</p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>

    {/* Credit Education FAQs */}
    <section id="credit-education-faqs" className="py-20 bg-white scroll-mt-24">
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-start">
        {/* FAQs - left column */}
        <div>
          <div className="mb-10">
            <div className="inline-flex items-center gap-2 mb-3">
              <BookOpen size={18} className="text-knight-gold" />
              <p className="text-knight-gold font-semibold tracking-widest text-xs">CREDIT EDUCATION</p>
            </div>
            <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-knight-navy mb-3 whitespace-nowrap">Credit Education FAQs</h2>
            <p className="text-knight-navy/70">The fundamentals every consumer should know about credit &mdash; straight, no jargon.</p>
          </div>
          <Accordion type="single" collapsible className="space-y-3">
            {creditEducationFaqs.map((f, i) => (
              <AccordionItem key={i} value={`ce-${i}`} className="faq-hover border border-slate-200 rounded-xl px-5 data-[state=open]:border-knight-navy data-[state=open]:bg-slate-50/50 transition-colors">
                <AccordionTrigger className="text-left font-semibold text-knight-navy hover:no-underline">{f.q}</AccordionTrigger>
                <AccordionContent className="text-knight-navy/75 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>

        {/* Team conference picture - right column, sticky */}
        <div className="lg:sticky lg:top-28">
          <div className="relative">
            <div className="absolute -inset-4 bg-knight-gold/15 rounded-3xl rotate-2" />
            <img
              src="https://images.pexels.com/photos/3184325/pexels-photo-3184325.jpeg?auto=compress&cs=tinysrgb&w=1400"
              alt="Our team meeting around a conference table"
              className="relative w-full h-[560px] object-cover rounded-3xl shadow-2xl"
              onError={(e) => { e.currentTarget.src = "https://images.pexels.com/photos/3183150/pexels-photo-3183150.jpeg?auto=compress&cs=tinysrgb&w=1400"; }}
            />
            <div className="absolute -bottom-6 -left-6 bg-knight-navy text-white rounded-2xl shadow-xl p-5 max-w-[240px] hidden sm:block border border-knight-gold/20">
              <p className="text-knight-gold text-[10.5px] font-bold tracking-[0.22em] mb-1">EXPERT STRATEGY</p>
              <p className="font-display text-base font-bold leading-snug">Every case is reviewed and strategized by a real human team.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section className="py-20 subtle-gradient">
      <div className="max-w-7xl mx-auto px-4 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-knight-gold font-semibold tracking-widest text-xs mb-3">WHAT'S INCLUDED</p>
          <h2 className="font-display text-4xl lg:text-5xl font-bold text-knight-navy mb-6">Every plan comes with <em>full support</em></h2>
          <ul className="grid sm:grid-cols-2 gap-3">
            {[
              "Three-bureau reports & scores",
              "Unlimited bureau disputes",
              "Inquiry dispute letters",
              "Debt validation letters",
              "Cease & desist letters",
              "Personal info corrections",
              "Notarized POA letters",
              "Every-60-day reviews",
              "24/7 mobile app access",
              "Cancel anytime"
            ].map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-knight-navy/85">
                <CheckCircle2 className="text-knight-gold flex-shrink-0 mt-0.5" size={18} />
                <span className="text-sm">{f}</span>
              </li>
            ))}
          </ul>
          <Button className="mt-8 bg-knight-navy hover:bg-knight-navy-dark text-white btn-lift">
            Schedule A Consultation <ArrowRight size={16} className="ml-2" />
          </Button>
        </div>
        <div className="relative">
          <img
            src="https://customer-assets.emergentagent.com/job_d3492e73-7acd-4186-bd61-8b4a9936405d/artifacts/ein5bf7w_create-a-chart-showing-a-team-of-7-of-di_wnsRrN0FVRejEH1E8qBHiQ_1ac8h0hMRUm_mXSQxpl7_g_cover.png"
            alt="The Knight Credit Consultants team presenting a credit score recovery journey chart"
            className="rounded-3xl shadow-2xl w-full h-[520px] object-cover"
          />
          <div className="absolute -bottom-6 -right-6 bg-knight-navy text-white rounded-2xl shadow-xl p-5 max-w-[230px]">
            <p className="font-display text-2xl font-bold text-knight-gold">Average +180 pts</p>
            <p className="text-xs text-white/70 mt-1">Score increase reported by our clients within 6 months</p>
          </div>
        </div>
      </div>
    </section>

    <CTABanner />
  </main>
);

export default Services;
